import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { adminPages } from '../lib/pageTitles'
import { clientPath, projectPath, invoicePath } from '../lib/recordLinks'

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n || 0)

function Highlight({ text, query }) {
  if (!query || !text) return text || null
  const i = text.toLowerCase().indexOf(query.toLowerCase())
  if (i < 0) return text
  return <>{text.slice(0, i)}<mark style={{ background: '#FEF08A', color: 'inherit', borderRadius: 2, padding: '0 1px' }}>{text.slice(i, i + query.length)}</mark>{text.slice(i + query.length)}</>
}

export default function CommandPalette({ onClose }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [cursor, setCursor] = useState(0)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const search = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return }
    setLoading(true)
    const [{ data: projects }, { data: clients }, { data: invoices }] = await Promise.all([
      supabase.from('projects').select('id, title, stage, clients(name)').ilike('title', `%${q}%`).limit(5),
      supabase.from('clients').select('id, name, company').or(`name.ilike.%${q}%,company.ilike.%${q}%`).limit(5),
      supabase.from('invoices').select('id, invoice_number, amount, status, clients(name)').ilike('invoice_number', `%${q}%`).limit(5),
    ])
    // Pages first: typing "invoices" or "time" should go there, not search for
    // a record that happens to share the word.
    const needle = q.trim().toLowerCase()
    const pages = adminPages()
      .filter(pg => pg.title.toLowerCase().includes(needle))
      .slice(0, 5)
      .map(pg => ({ type: 'page', id: `page:${pg.path}`, label: pg.title, sub: 'Go to page', icon: '↗', action: () => navigate(pg.path) }))
    const items = [
      ...pages,
      ...(projects || []).map(p => ({ type: 'project', id: p.id, label: p.title || 'Untitled', sub: `${p.clients?.name || '—'} · ${p.stage || '—'}`, icon: '📁', action: () => navigate(projectPath(p.id)) })),
      ...(clients || []).map(c => ({ type: 'client', id: c.id, label: c.name || '—', sub: c.company || '', icon: '👤', action: () => navigate(clientPath(c.id)) })),
      ...(invoices || []).map(i => ({ type: 'invoice', id: i.id, label: i.invoice_number || `#${i.id?.slice(-6).toUpperCase()}`, sub: `${i.clients?.name || '—'} · ${fmt(i.amount)} · ${i.status}`, icon: '🧾', action: () => navigate(invoicePath(i.id)) })),
    ]
    setResults(items)
    setCursor(0)
    setLoading(false)
  }, [navigate])

  useEffect(() => {
    const t = setTimeout(() => search(query), 220)
    return () => clearTimeout(t)
  }, [query, search])

  function go(item) { item.action(); onClose() }

  function onKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, items.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)) }
    // Enter takes whatever is highlighted, quick actions included; it used to
    // read only search results, so an empty query's list could not be chosen.
    else if (e.key === 'Enter') { e.preventDefault(); if (items[cursor]) go(items[cursor]) }
    else if (e.key === 'Escape') onClose()
  }

  useEffect(() => {
    if (listRef.current) {
      const el = listRef.current.children[cursor]
      el?.scrollIntoView({ block: 'nearest' })
    }
  }, [cursor])

  const QUICK_ACTIONS = [
    { label: 'New Project',  icon: '📁', action: () => navigate('/projects?new=1') },
    { label: 'New Invoice',  icon: '🧾', action: () => navigate('/invoices?new=1') },
    { label: 'New Proposal', icon: '📄', action: () => navigate('/proposals?new=1') },
    { label: 'Log Time',     icon: '⏱',  action: () => navigate('/time') },
    { label: 'Home',         icon: '🏠',  action: () => navigate('/dashboard') },
    { label: 'All Clients',  icon: '👥',  action: () => navigate(clientPath()) },
    { label: 'Client Updates', icon: '📊', action: () => navigate('/reports') },
    { label: 'Settings',     icon: '⚙️',  action: () => navigate('/settings') },
  ]

  const showQuick = !query.trim()
  const items = showQuick ? QUICK_ACTIONS.map((a, i) => ({ ...a, id: i, type: 'quick', sub: '' })) : results

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh' }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 560, background: 'var(--card)', borderRadius: 14, boxShadow: '0 24px 80px rgba(0,0,0,0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--bdr)' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: 'var(--hint)' }}>
            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10 10l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Go to a page, or search projects, clients, invoices…"
            style={{ flex: 1, border: 'none', outline: 'none', fontFamily: 'var(--fu)', fontSize: 15, color: 'var(--txt)', background: 'transparent' }}
          />
          {loading && <div className="spinner" style={{ width: 14, height: 14 }} />}
          <kbd style={{ flexShrink: 0, fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)', background: 'var(--off)', border: '1px solid var(--bdr)', borderRadius: 4, padding: '2px 6px' }}>ESC</kbd>
        </div>

        {/* Results / quick actions */}
        <div ref={listRef} style={{ maxHeight: 380, overflowY: 'auto' }}>
          {showQuick && (
            <div style={{ padding: '8px 18px 4px', fontFamily: 'var(--fu)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--hint)' }}>
              Quick Actions
            </div>
          )}
          {items.length === 0 && !loading && query.trim() && (
            <div style={{ padding: '28px 20px', textAlign: 'center', fontFamily: 'var(--fu)', fontSize: 13, color: 'var(--hint)' }}>
              No results for "{query}"
            </div>
          )}
          {items.map((item, i) => (
            <div
              key={item.id}
              onClick={() => go(item)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', cursor: 'pointer', background: cursor === i ? 'var(--off)' : 'transparent', transition: 'background 0.1s' }}
              onMouseEnter={() => setCursor(i)}
            >
              <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1 }}>{item.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--fu)', fontSize: 13, fontWeight: 500, color: 'var(--txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Highlight text={item.label} query={query} />
                </div>
                {item.sub && <div style={{ fontFamily: 'var(--fu)', fontSize: 11, color: 'var(--hint)', marginTop: 1 }}>{item.sub}</div>}
              </div>
              <kbd style={{ flexShrink: 0, fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)', background: 'var(--off)', border: '1px solid var(--bdr)', borderRadius: 4, padding: '2px 6px' }}>↵</kbd>
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div style={{ padding: '8px 18px', borderTop: '1px solid var(--bdr)', display: 'flex', gap: 14, background: 'var(--off)' }}>
          {[['↑↓', 'navigate'], ['↵', 'open'], ['Esc', 'close']].map(([key, label]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <kbd style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)', background: 'var(--card)', border: '1px solid var(--bdr)', borderRadius: 4, padding: '2px 6px' }}>{key}</kbd>
              <span style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
